<script lang="ts" setup>
import type { Galleries } from '~/types/MainPage';

const {
  data,
  compactReducedHeight = false,
  desktopColumns = 3,
  layoutVariant = 'standard',
  showHeader = true,
} = defineProps<{
  data: Galleries;
  compactReducedHeight?: boolean;
  desktopColumns?: 3 | 4;
  layoutVariant?: 'standard' | 'compact';
  showHeader?: boolean;
}>();

const appLink = resolveComponent('AppLink');
</script>

<template>
  <section class="tiles">
    <template v-if="showHeader && data.title">
      <AppLink
        v-if="data.url"
        :to="makeUrlRelative(data.url)"
        class="w-fit"
      >
        <h2 class="inline underline underline-offset-4">
          {{ data.title }}
        </h2>
      </AppLink>

      <h3
        v-else
        class="inline"
      >
        {{ data.title }}
      </h3>
    </template>

    <template v-if="layoutVariant === 'compact'">
      <div class="tiles__compact-grid tiles__compact-grid--mobile grid grid-cols-2 gap-0 md:hidden">
        <component
          :is="item.url ? appLink : 'div'"
          v-for="item in data.gallery_mobile"
          :key="item.url || item.full"
          :class="{
            'hover:opacity-80': item.url,
          }"
          :to="item.url ? makeUrlRelative(item.url) : undefined"
          class="tiles__compact-item relative min-w-0 overflow-hidden transition-opacity"
        >
          <picture class="block w-full">
            <source
              :srcset="item.thumb_webp.thumb_350"
              type="image/webp"
            />
            <img
              class="block h-auto w-full"
              :alt="item.alt"
              :src="item.thumb.thumb_350 || item.full"
            />
          </picture>

          <div class="tiles__text-over-image">
            <div
              v-if="item.text_over_image_small"
              class="font-bold"
            >
              {{ item.text_over_image_small }}
            </div>

            <div
              v-if="item.text_over_image_big"
              class="font-bold text-2xl leading-none"
            >
              {{ item.text_over_image_big }}
            </div>
          </div>
        </component>
      </div>

      <div class="tiles__compact-grid tiles__compact-grid--desktop hidden md:grid md:grid-cols-4 gap-0">
        <component
          :is="item.url ? appLink : 'div'"
          v-for="item in data.gallery_desktop"
          :key="item.url || item.full"
          :class="{
            'hover:opacity-80': item.url,
            'tiles__compact-item--reduced-height': compactReducedHeight,
          }"
          :to="item.url ? makeUrlRelative(item.url) : undefined"
          class="tiles__compact-item relative min-w-0 aspect-[110/60] overflow-hidden transition-opacity"
        >
          <picture>
            <source
              :srcset="`${item.thumb_webp.thumb_390}, ${item.thumb_webp.thumb_780} 2x`"
              type="image/webp"
            />
            <img
              class="size-full object-cover"
              :alt="item.alt"
              :src="item.full"
            />
          </picture>

          <div class="tiles__text-over-image">
            <div
              v-if="item.text_over_image_small"
              class="font-bold"
            >
              {{ item.text_over_image_small }}
            </div>

            <div
              v-if="item.text_over_image_big"
              class="font-bold text-2xl leading-none"
            >
              {{ item.text_over_image_big }}
            </div>
          </div>
        </component>
      </div>
    </template>

    <template v-else>
      <div class="grid md:hidden grid-cols-1 sm:grid-cols-2 grid-rows-auto justify-items-center gap-2">
        <AppLink
          v-for="item in data.gallery_mobile"
          :key="item.url"
          :class="{ 'hover:opacity-80': item.url }"
          :to="makeUrlRelative(item.url)"
          class="relative transition-opacity rounded-xl overflow-hidden"
        >
          <picture>
            <source
              :srcset="item.thumb_webp.thumb_350"
              type="image/webp"
            />

            <source
              :srcset="item.thumb.thumb_350"
              type="image/jpeg"
            />

            <img
              :alt="item.alt"
              :src="item.thumb.thumb_350 || item.full"
            />
          </picture>

          <div class="tiles__text-over-image">
            <div
              v-if="item.text_over_image_small"
              class="font-bold"
            >
              {{ item.text_over_image_small }}
            </div>

            <div
              v-if="item.text_over_image_big"
              class="font-bold text-2xl leading-none"
            >
              {{ item.text_over_image_big }}
            </div>
          </div>
        </AppLink>
      </div>

      <div
        :class="desktopColumns === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'"
        class="hidden md:grid grid-cols-3 grid-rows-auto gap-4"
      >
        <AppLink
          v-for="item in data.gallery_desktop"
          :key="item.url"
          :class="{ 'hover:opacity-80': item.url }"
          :to="makeUrlRelative(item.url)"
          class="relative min-w-0 aspect-[110/60] transition-opacity rounded-2xl overflow-hidden"
        >
          <picture>
            <source
              :srcset="`${item.thumb_webp.thumb_390}, ${item.thumb_webp.thumb_780} 2x`"
              type="image/webp"
            />

            <source
              :srcset="item.thumb.thumb_390"
              type="image/jpeg"
            />

            <img
              class="size-full object-cover"
              :alt="item.alt"
              :src="item.full"
            />
          </picture>

          <div class="tiles__text-over-image">
            <div
              v-if="item.text_over_image_small"
              class="font-bold"
            >
              {{ item.text_over_image_small }}
            </div>

            <div
              v-if="item.text_over_image_big"
              class="font-bold text-2xl leading-none"
            >
              {{ item.text_over_image_big }}
            </div>
          </div>
        </AppLink>
      </div>
    </template>
  </section>
</template>

<style lang="scss" scoped>
.tiles {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__compact-grid {
    overflow: hidden;
    border-radius: 16px;
  }

  &__compact-item--reduced-height {
    aspect-ratio: 110 / 20;
  }

  &__text-over-image {
    position: absolute;
    top: 50%;
    right: 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: flex-end;
    color: $white;
    transform: translateY(-50%);

    @include screen-md {
      gap: 8px;
    }
  }
}
</style>
